<template>
  <v-card dark class="edit organization" v-if="organization">
    <v-card-title primary-title>
      <v-tooltip bottom>
        <CoverImageUpload
          slot="activator"
          :src="organization.coverImage"
          :path="`/admin/organizations/${organization.id}/cover_image`"
          @success="coverImageSuccess"
        />
        <span>Click to upload cover image</span>
      </v-tooltip>
      <div>
        <div class="headline">{{ organization.name }}</div>
        <span class="subheading grey--text">{{ organization.slug }}</span>
      </div>
      <v-spacer />
      <v-tooltip bottom>
        <v-btn slot="activator" @click="$emit('cancel')" icon><v-icon>mdi-close</v-icon></v-btn>
        <span>Click to close without saving</span>
      </v-tooltip>
    </v-card-title>

    <v-alert color="error" transition="scale-transition" :value="organization.errors">
      <ul>
        <li v-for="(message, i) in organization.errors" :key="`error-${i}`">{{ message }}</li>
      </ul>
    </v-alert>

    <v-form v-model="valid" @submit.prevent="submit">
      <v-card-text>
        <v-text-field label="Name" v-model="organization.name" :rules="rules.name" />
        <v-text-field label="Slug" v-model="organization.slug" :rules="rules.slug" required />

        <v-autocomplete
          multiple
          label="Industries"
          v-model="organization.industries"
          :items="industries"
        />
        <v-select clearable label="Size" v-model="organization.size" :items="sizes" />
        <v-text-field label="Website" v-model="organization.website" />
        <v-text-field
          clearable
          label="Promotional Video"
          v-model="organization.video"
          hint="Provide the full URL to the YouTube or Vimeo page"
        />
        <v-text-field
          clearable
          label="Listing ID"
          v-model="organization.listing"
          hint="Provide only the ID of the listing on Leaply Listings"
        />
        <v-text-field
          label="Facebook Page"
          v-model="organization.facebook"
          hint="Provide the full URL to the Facebook page"
        />
        <v-text-field
          v-model="organization.twitter"
          label="Twitter Page"
          hint="Provide the full URL to the Twitter page"
        />

        <v-text-field
          v-model="organization.linkedin"
          label="LinkedIn Page"
          hint="Provide the full URL to the LinkedIn page"
        />
        <v-text-field
          label="Instagram Page"
          v-model="organization.instagram"
          hint="Provide the full URL to the Instagram page"
        />

        <v-text-field
          v-model="organization.location.address"
          label="Headquarters"
          :rules="rules.location"
        />

        <v-divider />
        <v-textarea auto-grow v-model="organization.summary" label="Summary" />
        <v-text-field
          v-model="organization.blurb"
          label="Short Blurb"
          :rules="rules.blurb"
          counter="256"
        />
        <v-textarea auto-grow v-model="organization.culture" label="Culture" />

        <v-alert outline type="info" v-if="organization.publishedAt">
          Organization published at
          {{ new Date(organization.publishedAt) | date('h:mm a, MMMM do yyyy') }}
          ({{ new Date(organization.publishedAt) | inWords({ addSuffix: true }) }})
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-btn flat v-if="organization.publishedAt" @click="$emit('unpublish')">
          <v-icon left>mdi-earth-off</v-icon> Unpublish
        </v-btn>
        <v-btn v-else flat @click="$emit('publish')">
          <v-icon left>mdi-earth</v-icon> Publish
        </v-btn>

        <v-spacer />
        <v-btn flat @click="$emit('cancel')" color="grey">Cancel</v-btn>
        <v-btn @click="submit" :disabled="!valid" color="primary">
          Update Organization
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
// Components
import CoverImageUpload from '@/components/CoverImageUpload'

// Data
import industries from '@/data/industries'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  components: { CoverImageUpload },
  data() {
    return {
      publishing: false,
      unpublish: false,
      // Validation
      valid: true,
      rules: {
        name: [v => !!v || 'must be specified'],
        slug: [
          v => !!v || 'must be specified',
          v => !/\s+/.exec(v) || 'may not contain whitespace',
        ],
        blurb: [v => !v || v.length < 256 || 'must be shorter than 256 characters'],
        location: [v => !v || v.length < 128 || 'must be shorter than 256 characters'],
      },
      // Options
      sizes: ['1 - 15', '16 - 50', '51 - 200', '201 - 500', '501 - 1000', '1000+'],
      industries: industries,
    }
  },
  computed: mapGetters({ organization: 'admin/organizations/find' }),
  methods: {
    submit() {
      if (this.valid) {
        if (this.unpublish) Object.assign(this.organization, { publishedAt: null })
        this.$emit('update')
      }
    },
    coverImageSuccess() {
      this.$store.dispatch('admin/organizations/fetch', this.organization.id)
    },
  },
}
</script>
